import React, { useContext, useRef } from 'react';
import get from 'lodash/get';
import GlobalContext from 'hooks/contexts/GlobalContext';
import cx from 'utils/classnames';
import Wysiwyg from 'components/blocks/wysiwyg/Wysiwyg';
import getFormSelectionObjects from './utils/getFormSelectionObjects';
import SubmissionCart from '../micro-portal/SubmissionCart';

const MAX_CATEGORY_LABEL_LENGTH = 32;

interface ClickPortalSearchHeaderProps {
  title?: string;
  subTitle?: string;
  showTitle: boolean;
}

function ClickPortalSearchHeader(
  props: ClickPortalSearchHeaderProps
): React.JSX.Element | null {
  const { title, subTitle, showTitle } = props;
  const {
    programTaxonomyMap,
    clickPortal: { currentSelection, isLoading },
    siteMeta: {
      siteType,
      clickPortalSettings: { isMultiRow, searchHeaderDescription },
    },
    microPortal: { submittedSchools },
    windowSize: { currentBreakpoint = 'mobile' },
  } = useContext(GlobalContext);
  const lastTitle = useRef<{ title?: string; subTitle?: string }>({});

  const isMobile = currentBreakpoint === 'mobile';
  const searchHeaderDescriptionHtml = isMobile
    ? searchHeaderDescription.mobile
    : searchHeaderDescription.desktop;
  const { degree, category, subject } = getFormSelectionObjects(
    currentSelection,
    programTaxonomyMap
  );

  const hasSelection = category && degree && subject;
  let _title = title;
  let _subTitle = subTitle;
  // TODO: use useTokens hook here to clean this up
  if (isMultiRow && hasSelection) {
    const categoryLabel = get(category, 'label', '');
    const degreeLabel = get(degree, 'label', '');

    // Truncate title if category label is longer than the maximum allowed length
    if (isMobile || categoryLabel.length > MAX_CATEGORY_LABEL_LENGTH) {
      _title = `Find Colleges With Online ${degreeLabel} Programs in ${categoryLabel}`;
    } else {
      _title = `Find Colleges and Universities With Online ${degreeLabel} Programs in ${categoryLabel}`;
    }
    _subTitle = '';
  } else if (!isMultiRow && hasSelection && !title) {
    _title = `${get(degree, 'label')}s in ${get(category, 'label')}`;
    _subTitle = 'Our Picks for Online Colleges with';
  }

  // An incomplete filter change clears the selection before the next results
  // load. Keep the title that still matches the results on the page.
  if (_title || _subTitle) {
    lastTitle.current = { title: _title, subTitle: _subTitle };
  } else {
    _title = lastTitle.current.title;
    _subTitle = lastTitle.current.subTitle;
  }

  if (!showTitle) {
    return null;
  }
  return (
    <div
      className={cx('clickPortalSearchHeader', {
        'clickPortalSearchHeader--isLoading': isLoading,
      })}
    >
      {siteType === 'microPortal' && submittedSchools.length > 0 && (
        <div className="clickPortalSearchHeader__cart">
          <SubmissionCart isStatic title={_title} />
        </div>
      )}
      {showTitle && (
        <h1 className="clickPortalSearchHeader__title">
          <span className="clickPortalSearchHeader__preamble">{_subTitle}</span>
          <span className="clickPortalSearchHeader__titleText">{_title}</span>
        </h1>
      )}
      {searchHeaderDescriptionHtml && (
        <div className="clickPortalSearchHeader__description">
          <Wysiwyg isClean content={searchHeaderDescriptionHtml} />
        </div>
      )}
    </div>
  );
}

export default ClickPortalSearchHeader;
